import { useState, useEffect, useRef , createRef } from "react";

import MatchingGameCard from "./MatchingGame_Card";
import "./MatchingGame.css"
import HeaderNav from "../../Components/Header/Header_Nav/Header_Nav";
import Footer from "../../Components/Footer/Footer";
import { themeEmojis } from "./ThemeEmojis";

//sound
import correct_ding from "../../assets/GameAssets/MatchingGame/correct_ding.mp3";
import flip_sound from "../../assets/GameAssets/MatchingGame/flip_sound.m4a";

//Scenes
import LovelyPark from "../../assets/GameAssets/MatchingGame/LovelyPark.png";
import SleepyCafe from "../../assets/GameAssets/MatchingGame/SleepyCafe.png";
import CozyKitchen from "../../assets/GameAssets/MatchingGame/CozyKitchen.png";
import StarryHighrise from "../../assets/GameAssets/MatchingGame/StarryHighrise.png";
import ShadyVillage from "../../assets/GameAssets/MatchingGame/ShadyVillage.png";

//Tables
import PladTable from "../../assets/GameAssets/MatchingGame/PladTable.png";
import SleepyCafeTable from "../../assets/GameAssets/MatchingGame/SleepyCafeTable.png";
import LovelyParkTable from "../../assets/GameAssets/MatchingGame/LovelyParkTable.png";
import StarryHighriseTable from "../../assets/GameAssets/MatchingGame/StarryHighriseTable.png";

//Cards
import MatchingCardHeart from "../../assets/GameAssets/MatchingGame/MatchingCardHeart.png";
import MatchingCardMug from "../../assets/GameAssets/MatchingGame/MatchingCardMug.png";
import MatchingCardTaeguk from "../../assets/GameAssets/MatchingGame/MatchingCardTaeguk.png";
import MatchingCardStar from "../../assets/GameAssets/MatchingGame/MatchingCardStar.png";
import MatchingGameStarNight_Neon from "../../assets/GameAssets/MatchingGame/MatchingGameStarNight_Neon.png";
import MatchingCardSakura from "../../assets/GameAssets/MatchingGame/MatchingCardSakura.png";

//Card Data
import { vowels_mg } from "./MatchingCardData";
import { consonants_mg } from "./MatchingCardData";
import { pairs_mg } from "./MatchingCardData";

//SP Loader
import SPLoader from "../../Components/Spinner/SpinnerLoader";

const themeStyles = {
  park: {
    label: "Lovely Park",
    board: LovelyParkTable,
    cardBack: MatchingCardSakura
  },
  kitchen: {
    label: "Cozy Kitchen",
    board: PladTable,
    cardBack: MatchingCardHeart,
  },
  coffee: {
    label: "Sleepy Café",
    board: SleepyCafeTable,
    cardBack: MatchingCardMug,
  },
  village: {
    label: "Shady Village",
    board: LovelyParkTable,
    cardBack: MatchingCardTaeguk,
  },
  highrise: {
    label: "Starry Highrise",
    board: StarryHighriseTable,
    cardBack: MatchingGameStarNight_Neon,
  },
};

function MatchingGame_Cards() {
  
  const [difficulty, setDifficulty] = useState(null); // "easy" | "medium" | "hard" | null
  const [items, setItems] = useState([]); // card objects: { id, text, stat }
  const [prev, setPrev] = useState(null); // index of previously selected card
  const [score, setScore] = useState(0);
  const [showWin, setShowWin] = useState(false);
  const [cardType, setCardType] = useState("vowels_mg");
  const [previewTime, setPreviewTime] = useState(2000); // default 2 sec
  const [lockBoard, setLockBoard] = useState(false); // <-- add this line
  const [gridSize, setGridSize] = useState("Easy");
  const [showScenePreview, setShowScenePreview] = useState(false);
  
  //timer option
  const [timeLimit, setTimeLimit] = useState(null); // milliseconds
  const [timeSelect, setTimeSelect] = useState("none"); // "1min" | "2min" | "none"
  const [timeLeft, setTimeLeft] = useState(null); // countdown in milliseconds
  const timerRef = useRef(null);

  //themes
  const [theme, setTheme] = useState(null); 
  const themes = [
  { id: "park", label: "Lovely Park", imgSrc: LovelyPark },
  { id: "kitchen", label: "Cozy Kitchen", imgSrc: CozyKitchen },
  { id: "coffee", label: "Sleepy Cafe", imgSrc: SleepyCafe},
  { id: "village", label: "Shady Village", imgSrc: ShadyVillage },
  { id: "highrise", label: "Starry Highrise", imgSrc: StarryHighrise },

];

const themePreviewImage = themes.find(t => t.id === theme)?.imgSrc || LovelyPark;

const contentSets = {
  vowels_mg,
  consonants_mg,
  pairs_mg,
};

const cardSets = {
  easy: ["A", "B", "C", "D"],                     // 4 pairs
  medium: ["A", "B", "C", "D", "E", "F"],         // 6 pairs
  hard: ["A", "B", "C", "D", "E", "F", "G", "H"], // 8 pairs
};

const gridLayouts = {
  Easy: { rows: 2, columns: 3 },   // 3x2
  Medium: { rows: 3, columns: 4 }, // 4x3 
  Hard: { rows: 4, columns: 4 },   // 4x4
};

//Emoji Burst
const boardRef = useRef(null);
const cardRef = createRef(); 

//sounds
//correct sound
const correctSoundRef = useRef(null);

useEffect(() => {
  const audio = new Audio(correct_ding);
  audio.volume = 1.0;
  audio.preload = "auto";        
  audio.load();//force preload               
  correctSoundRef.current = audio;
}, []);


function playCorrectSound() {
  const sound = correctSoundRef.current;
  if (!sound) return;

  sound.pause();           
  sound.currentTime = 0;    

  sound.play().catch((err) =>
    console.warn("Audio blocked until user interacts:", err)
  );
}

//flip sound
const flipSoundRef = useRef(null);

useEffect(() => {
  const audio = new Audio(flip_sound);
  audio.volume = 0.2; 
  audio.preload = "auto";
  audio.load();
  flipSoundRef.current = audio;
}, []);


function playFlipSound() {
  const sound = flipSoundRef.current;
  if (!sound) return;

  sound.pause();//reset if already playing
  sound.currentTime = 0;
  sound.play().catch((err) => console.warn("Audio blocked until user interacts:", err));
}


function getScoreMultiplier() {
  let multiplier = 1;

  // Preview time: less preview time = higher multiplier
  if (previewTime <= 500) multiplier *= 2;
  else if (previewTime <= 1000) multiplier *= 1.5;
  else if (previewTime <= 2000) multiplier *= 1.2;

  // Grid size: harder grid = higher multiplier
  if (gridSize === "Medium") multiplier *= 1.5;
  else if (gridSize === "Hard") multiplier *= 2;

  // Timed challenge: adds bonus if timer is active
  if (timeLimit) multiplier *= 1.3;

  return multiplier;
}

function handleClick(index, cardElement) {
  if (!items[index] || !cardElement) return;
  if (lockBoard || items[index].stat === "active" || items[index].stat === "correct") return;

    playFlipSound(); 
  
    setItems((oldItems) => {
    const newItems = oldItems.map((it, idx) =>
      idx === index ? { ...it, stat: "active" } : it
    );

    // first card selected
    if (prev === null) {
      setPrev(index);
      return newItems;
    }

    const firstIndex = prev;
    const secondIndex = index;
    setLockBoard(true);

    const first = newItems[firstIndex];
    const second = newItems[secondIndex];

    if (!first || !second) return newItems;

    if (first.id === second.id) {
      
      //correct match
      const multiplier = getScoreMultiplier();
      setScore((s) => s + Math.floor(10 * multiplier));

      // play sound
       playCorrectSound();

      // delay to allow flip animation
      setTimeout(() => {
        // mark both cards as correct
        setItems((curr) =>
          curr.map((it, idx) =>
            idx === firstIndex || idx === secondIndex
              ? { ...it, stat: "correct" }
              : it
          )
        );

        // trigger emoji burst on last card
        const cardEl = document.querySelectorAll(".card-mg")[secondIndex];
        if (cardEl) {
          const rect = cardEl.getBoundingClientRect();
          const x = rect.left + rect.width / 2;
          const y = rect.top + rect.height / 2;
          createEmojiBurst(x, y, theme); // screen coordinates
        }

        setPrev(null);
        setLockBoard(false);
      }, 300); // match flip animation duration

    } else {
      // not a match
      setScore((s) => Math.max(0, s - 2));
      setTimeout(() => {
        setItems((curr) =>
          curr.map((it, idx) =>
            idx === firstIndex || idx === secondIndex ? { ...it, stat: "" } : it
          )
        );
        setPrev(null);
        setLockBoard(false);
      }, 1000); // time for flip-back animation
    }

    return newItems;
  });
}

function createEmojiBurst(x, y, theme) {
  const container = document.createElement("div");
  container.className = "emoji-burst";
  container.style.position = "absolute";

  //manually adjust animation positioning
  container.style.left = `${x}px`;
  container.style.top = `${y}px`;

  container.style.pointerEvents = "none";
  container.style.transform = "translate(-50%, -50%)"; //center the burst
  container.style.overflow = "visible";
  container.style.zIndex = 1000;

  const emojis = themeEmojis[theme] || ["✨", "🎉", "💫"];

  for (let i = 0; i < 8; i++) {
    const emoji = document.createElement("span");
    emoji.textContent = emojis[Math.floor(Math.random() * emojis.length)];

    const angle = (i / 8) * Math.PI * 2;
    const radius = 50; //px around center
    emoji.style.position = "absolute";
    emoji.style.left = `${radius * Math.cos(angle)}px`;
    emoji.style.top = `${radius * Math.sin(angle)}px`;
    emoji.style.transition = "all 0.8s ease-out";
    container.appendChild(emoji);

    // Animate outwards
    setTimeout(() => {
      emoji.style.left = `${radius * 2 * Math.cos(angle)}px`;
      emoji.style.top = `${radius * 2 * Math.sin(angle)}px`;
      emoji.style.opacity = 0;
    }, 50);
  }

  document.body.appendChild(container);

  //Remove after animation
  setTimeout(() => container.remove(), 1000);
}

function startGame(level) {
  setDifficulty(true);
  setDifficulty(level);
  setScore(0);
  setShowWin(false);
  setPrev(null);  
  setLockBoard(false);

  const subsetSize = gridLayouts[gridSize].rows * gridLayouts[gridSize].columns / 2;
  const selectedSet = contentSets[cardType];
  const shuffledContent = [...selectedSet].sort(() => Math.random() - 0.5);
  const chosenContent = shuffledContent.slice(0, subsetSize);

  const pairArray = [];
  chosenContent.forEach((item, idx) => {
    pairArray.push({ id: idx, text: item.kor, type: "kor", stat: "" });
    pairArray.push({ id: idx, text: item.rom, type: "rom", stat: "" });
  });

  //shuffle
  const shuffledArray = pairArray.sort(() => Math.random() - 0.5);

  const initializedCards = shuffledArray.map(c => ({ ...c, stat: "" }));

  //scenic preview
  setShowScenePreview(true);

  setTimeout(() => {
    setShowScenePreview(false);

    if (previewTime > 0) {
      const previewItems = initializedCards.map((c) => ({ ...c, stat: "active" }));
      setItems(previewItems);
      setLockBoard(true);

      setTimeout(() => {
        setItems(initializedCards);
        setLockBoard(false);
      }, previewTime);
    } else {
      setItems(initializedCards);
      setLockBoard(false);
    }

    // start countdown
    if (timeLimit) setTimeLeft(timeLimit);
    else setTimeLeft(null);
  }, 1000);//was 2000
}

  //timer countdown
  useEffect(() => {
    if (!difficulty || timeLeft === null) return;

    if (timeLeft <= 0) {
      alert("Time's up!");
      setDifficulty(null); // go back to menu
      return;
    }

    timerRef.current = setTimeout(() => setTimeLeft(timeLeft - 1000), 1000);
    return () => clearTimeout(timerRef.current);
  }, [timeLeft, difficulty]);

  useEffect(() => {
    if (items.length && items.every((i) => i.stat === "correct")) {
      setShowWin(true);
      clearTimeout(timerRef.current); //stop the countdown immediately
    }
  }, [items]);

  function endGame() {
      clearTimeout(timerRef.current);
      setDifficulty(null);
      setTimeLeft(null);
      setShowWin(false);
    }

    //will be called when 'you win' pops up
    if (showWin) {
      clearTimeout(timerRef.current);
    }

  useEffect(() => {
    if (items.length && items.every((i) => i.stat === "correct")) {
      setShowWin(true);
    }
  }, [items]);

const columns = gridLayouts[gridSize]?.columns || 2;
const rows = gridLayouts[gridSize]?.rows || 3;

 return (
  <div>
    <HeaderNav GoBack_to="/" />

    <div className="content-container-mg">

      <SPLoader time={0.2}/>
      
      
      {/* Top Bar */}
      {difficulty && (
        <div className="top-bar-mg" style={{ display: "flex", gap: 16, marginBottom: "1rem" }}>
          <div className="score-display-mg">Score: {score}</div>
          <div>Multiplier: x{getScoreMultiplier().toFixed(1)}</div>
          {timeLeft !== null && <div className="timer-display-mg">Time: {Math.floor(timeLeft / 1000)}s</div>}
          <div>
            <button id="btn-restart-mg" onClick={() => startGame(difficulty)}>Restart</button>
            <button onClick={() => setDifficulty(null)}>Menu</button>
          </div>
        </div>
      )}

   {/* Game Board */}
    <div
      className={`table-container-mg ${!difficulty ? "blurred" : ""}`}
      style={{
        
        backgroundImage: `url(${!difficulty && theme ? themes.find(t => t.id === theme)?.imgSrc : (theme ? themeStyles[theme]?.board : PladTable)})`,
        height: "auto",
        margin: "0 auto",
        marginTop: "30px",
        display: "flex",
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        height: "70vh", 
        width: "110vh", 
        backgroundSize: "cover",
        backgroundPosition: "center",
        justifyContent: "center",
        alignItems: "flex-start", //move content to top
        paddingTop: "2rem",       
        overflow: "hidden",
        position: "relative",
        transition: "background-image 1s ease-in-out",
        border: "7px solid pink",
        borderRadius: "12px",
      }}
    >
      {/* Scenic Preview */}
      {showScenePreview && (
        <div className="scene-preview-overlay">
          <img
            src={themePreviewImage}
            alt="Scene Preview"
            className="scene-preview-image"
          />
        </div>
      )}

      {/* Game grid, hidden behind preview */}
      <div
        className="container-mg"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, auto)`,
          gap: "1rem",
          width: "80%",
          justifyContent: "center",
          height: "auto",
          placeItems: "center",
          opacity: showScenePreview ? 0 : 1,
          transition: "opacity 0.6s ease-in-out",
          alignContent: "start", //align grid w/ top of container --> "top"
          marginTop: gridSize === "Easy" ? "6rem" : "1.6rem",
        
        }}
      >
        {items.map((item, index) => {
           const cardRef = createRef(); 

          return (  

          <MatchingGameCard
            key={index}
            ref={cardRef} //attach ref to DOM node
            item={item}
            theme = {theme}
            id={index}
            handleClick={(idx, ref) => handleClick(idx, ref)} 
            onClick={() => handleClick(index, cardRef.current)}
            size={
              gridSize === "Easy" ? "medium" :
              gridSize === "Medium" ? "large" :
              "small"
            }
            cardBack={theme ? themeStyles[theme]?.cardBack : MatchingCardStar}
            cardRef={cardRef}    
          />
        );
      })}
      </div>
    </div>
      
      {/* Overlay Menu (when no difficulty chosen)*/}
      
      {!difficulty && (
        <div className="menu-overlay">
          <div className="game-menu-container">
            <h1 className="game-menu-title-mg">
              Hangeul Match
            </h1>

            <div className="menu-options" style={{ marginBottom: "1rem" }}>
                               
            <div>
              <label>Theme: </label>
              <div className="img-selection-options-container-mg">
                {themes.map((t) => (
                  <div
                    key={t.id}
                    className={`theme-selection-option-mg ${theme === t.id ? "selected" : ""}`}
                    onClick={() => setTheme(t.id)}
                  >
                    <img
                      className="theme-selection-options-mg"
                      src={t.imgSrc}
                      alt={t.label}
                    />
                    <div className="theme-label-mg">{t.label}</div>
                  </div>
                ))}
              </div>
            </div>

              <div>
                <label>Preview Time: </label>
                <select
                  value={previewTime}
                  onChange={(e) => setPreviewTime(Number(e.target.value))}
                >
                  <option value={2000}>2 sec</option>
                  <option value={1000}>1 sec</option>
                  <option value={500}>0.5 sec</option>
                  <option value={0}>No Preview</option>
                </select>
              </div>

              <div>
                <label>Card Type: </label>
                <select
                  value={cardType}
                  onChange={(e) => setCardType(e.target.value)}
                >
                  <option value="vowels_mg">Vowels</option>
                  <option value="consonants_mg">Consonants</option>
                  <option value="pairs_mg">Vowel + Consonant</option>
                </select>
              </div>
 
              <div>
                <label>Grid Size: </label>
                <select
                  value={gridSize}
                  onChange={(e) => setGridSize(e.target.value)}
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  {/* <option value="Hard">Hard</option> */}
                  {/* could not get to scale */}
                </select>
              </div>

              <div>
                <label>Timed: </label>
                <select
                  value={timeSelect}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTimeSelect(val);
                    if (val === "1min") setTimeLimit(60_000);
                    else if (val === "2min") setTimeLimit(120_000);
                    else setTimeLimit(null); // No timer
                  }}
                >
                  <option value="1min">1 min</option>
                  <option value="2min">2 min</option>
                  <option value="none">No Time</option>
                </select>
              </div>
         
            </div>

          <button
            className="start-game-button"
            onClick={startGame} 
          >
            Start Game
            </button>

        </div>
      </div>     
         
     )}

       {/* Win Popup */}
        {difficulty && showWin && (
          <div className="popup-overlay-mg">
            <div className="popup-content-mg">
              <h2>Great Job!</h2>
              <p>Final score: {score}</p>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => startGame(difficulty)}>Try Again</button>
                <button onClick={() => setDifficulty(null)}>Back to Menu</button>
              </div>
            </div>
          </div>
        )}
    </div>
    <Footer/>
  </div>
);}

export default MatchingGame_Cards;
